import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Tag,
  TagCloseButton,
  TagLabel,
  Wrap,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  CloseIcon,
} from '@cdl/heartbeat-react';
import { modifiersByCodeSelector } from '../selectors/diagnostic.selectors';
import { manualAbnormalitiesSelector } from '../selectors/user-diagnostic.selectors';
import { saveManualAbnormalities } from '../thunks/save-manual-abnormalities.thunk';
import {
  Abnormality,
  Frequency,
  Likelihood,
  Localization,
  Modifier,
  Temporality,
} from '../types/diagnostic';
import { hasModifiers } from '../utils/abnormalities.util';
import { DiagnosticModifier } from './DiagnosticModifier';
import { needToAssessSelector } from '../../autoLabels/selectors/auto-labels.selectors';

interface Props {
  abnormality: Abnormality;
  deleteAbnormality: (abnormality: Abnormality) => void;
}

export const DiagnosticLabel: React.FC<Props> = ({
  abnormality,
  deleteAbnormality,
}) => {
  const dispatch = useDispatch();
  const isFocusDisabled = useSelector(needToAssessSelector);
  const tabIndex = isFocusDisabled ? -1 : 0;
  const storedAbnormalities = useSelector(manualAbnormalitiesSelector);
  const abnormalitiesByCode = useMemo(
    () =>
      storedAbnormalities.reduce(
        (acc, storedAbnormality) => ({
          ...acc,
          [storedAbnormality.code]: storedAbnormality,
        }),
        {} as Record<string, Abnormality>,
      ),
    [storedAbnormalities],
  );
  const definitions = useSelector(modifiersByCodeSelector);
  const modifiers = definitions[abnormality.code];
  const withModifiersDefinition = useMemo(() => {
    if (modifiers == null) return false;
    return hasModifiers(modifiers);
  }, [modifiers]);
  const withModifiersSelected = useMemo(
    () =>
      hasModifiers({
        likelihood:
          abnormality.likelihood != null ? [abnormality.likelihood] : null,
        frequency:
          abnormality.frequency != null ? [abnormality.frequency] : null,
        temporality:
          abnormality.temporality != null ? [abnormality.temporality] : null,
        localization:
          abnormality.localization != null ? [abnormality.localization] : null,
      }),
    [abnormality],
  );
  const deleteLabel = useCallback(
    () => deleteAbnormality(abnormality),
    [abnormality, deleteAbnormality],
  );

  const menuGroups = useMemo(():
    | Record<Modifier, (Frequency | Temporality | Likelihood | Localization)[]>
    | Record<string, never> => {
    if (modifiers == null || !withModifiersDefinition) {
      return {};
    }

    return Object.keys(modifiers).reduce((acc, modifierName) => {
      const modifier = modifiers[modifierName as Modifier];
      return modifier == null
        ? acc
        : {
            ...acc,
            [modifierName]: modifier,
          };
    }, {});
  }, [withModifiersDefinition, modifiers]);

  const changeModifierValue = useCallback(
    (
      modifier: Modifier,
      modifierValue: Frequency | Temporality | Likelihood | Localization,
    ) => {
      const newValue =
        abnormalitiesByCode[abnormality.code][modifier] === modifierValue
          ? null
          : modifierValue;
      const newAbnormality = {
        ...abnormalitiesByCode[abnormality.code],
        [modifier]: newValue,
      };
      const updatedAbnormalities = {
        ...abnormalitiesByCode,
        [newAbnormality.code]: newAbnormality,
      };
      dispatch(saveManualAbnormalities(Object.values(updatedAbnormalities)));
    },
    [dispatch, abnormality, abnormalitiesByCode],
  );

  return (
    <>
      <Tag
        data-testid='diagnostic-label'
        cursor={
          modifiers != null && withModifiersDefinition ? 'pointer' : 'default'
        }
        size='sm'
      >
        <Menu closeOnSelect={false}>
          <MenuButton
            textAlign='left'
            title={abnormality.name}
            tabIndex={tabIndex}
          >
            <TagLabel>{abnormality.name}</TagLabel>
          </MenuButton>
          <MenuList data-testid='modifier-dropdown'>
            {Object.keys(menuGroups).map((menuGroup) => (
              <MenuOptionGroup
                textTransform='uppercase'
                title={menuGroup}
                type='radio'
                key={menuGroup}
                value={abnormality[menuGroup as Modifier] as string}
              >
                {menuGroups[menuGroup as Modifier].map((modifierValue) => (
                  <MenuItemOption
                    key={modifierValue}
                    fontSize='s'
                    value={
                      modifierValue as NonNullable<
                        Frequency | Temporality | Likelihood | Localization
                      >
                    }
                    onClick={() => {
                      changeModifierValue(menuGroup as Modifier, modifierValue);
                    }}
                  >
                    {modifierValue}
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
            ))}
          </MenuList>
        </Menu>
        <TagCloseButton tabIndex={tabIndex}>
          <CloseIcon
            onClick={deleteLabel}
            data-testid='clear-diagnostic-label'
          />
        </TagCloseButton>
      </Tag>
      {modifiers != null && withModifiersSelected && (
        <Wrap
          borderLeftWidth='medium'
          borderColor='neutral.25'
          pt='s'
          pl='s'
          ml='s'
        >
          {abnormality.likelihood && (
            <DiagnosticModifier
              modifier='likelihood'
              modifierValue={abnormality.likelihood}
              abnormality={abnormality}
            />
          )}
          {abnormality.frequency && (
            <DiagnosticModifier
              modifier='frequency'
              modifierValue={abnormality.frequency}
              abnormality={abnormality}
            />
          )}
          {abnormality.temporality && (
            <DiagnosticModifier
              modifier='temporality'
              modifierValue={abnormality.temporality}
              abnormality={abnormality}
            />
          )}
          {abnormality.localization && (
            <DiagnosticModifier
              modifier='localization'
              modifierValue={abnormality.localization}
              abnormality={abnormality}
            />
          )}
        </Wrap>
      )}
    </>
  );
};
